import { create } from 'zustand';

// Theme store — light is the default. We persist the user's explicit choice
// to localStorage; if nothing is saved we still pick light (not system) so
// the playground always opens with a predictable look.

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'playground_theme';

function readSavedTheme(): Theme {
    if (typeof window === 'undefined') return 'light';
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        return v === 'dark' ? 'dark' : 'light';
    } catch {
        return 'light';
    }
}

function applyToDom(theme: Theme) {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    if (theme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
    // Help browser UI (e.g. mobile address bar / form controls) match.
    root.style.colorScheme = theme;
}

interface State {
    theme: Theme;
    setTheme: (t: Theme) => void;
    toggle: () => void;
    initFromStorage: () => void;
}

export const useThemeStore = create<State>((set, get) => ({
    theme: 'light',
    setTheme: (t) => {
        applyToDom(t);
        try { localStorage.setItem(STORAGE_KEY, t); } catch { /* private mode */ }
        set({ theme: t });
    },
    toggle: () => {
        const next: Theme = get().theme === 'dark' ? 'light' : 'dark';
        get().setTheme(next);
    },
    initFromStorage: () => {
        const t = readSavedTheme();
        applyToDom(t);
        set({ theme: t });
    },
}));
